/**
 * O carrossel da Nex Lab — aritmética pura, sem canvas e sem DOM.
 *
 * Mesma divisão de `layout.ts` (ver docs/EDITOR-DE-ARTES.md §1): aqui se
 * decide ONDE cada coisa vai e em que ordem os slides saem; quem desenha é
 * `src/ui/arte/nexlab.ts`. Coordenada errada é invisível no preview e torta
 * no perfil, então ela vira número e número ganha teste.
 *
 * O material de origem está em `docs/rec/POST NEX/`: as referências em JPEG,
 * os cards em SVG e a tipografia em `Texto espec.txt`. As medidas abaixo
 * foram tiradas das referências, que são 1080×1350 — por isso os tamanhos de
 * fonte da especificação, em pt, entram aqui como px sem conversão: o "pt" do
 * arquivo de design já é o pixel da arte.
 *
 * Diferente do sistema do AntiFila, quase nada aqui é desenhado por código: o
 * card, o `//`, a assinatura NEX.lab, a seta e a tag vêm prontos dos SVGs do
 * designer. Só o que VARIA é posicionado aqui — texto, fotos e a pílula do
 * parceiro, cuja largura acompanha o nome.
 *
 * Nada aqui importa nada.
 */

// ---------------------------------------------------------------- tokens

export const LARGURA = 1080;
export const ALTURA = 1350;

export const COR = {
  texto: "#FFFFFF",
  /** O roxo da marca, o mesmo `#A563FF` que tinge os cards dos SVGs. */
  roxo: "#A563FF",
  /** A pílula do parceiro, como estava no `Card Capa.svg`. */
  pilula: "rgba(10, 0, 22, 0.66)",
  /** A linha que segue o subtítulo. */
  linha: "rgba(165, 99, 255, 0.75)",
  /** Fundo de slot sem foto. Nunca vai para a exportação — ela recusa antes. */
  vazio: "#1A1426"
} as const;

/** Os arquivos estão em `public/fontes/` e as faces em `public/style.css`. */
export const FONTE = {
  titulo: '"Syne"',
  texto: '"Titillium Web"'
} as const;

/** `Texto espec.txt`, em px da arte. */
export const TIPO = {
  local: 26.72,
  parceiro: 35.17,
  titulo: 118.38,
  subtitulo: 47.3
} as const;

/** O peso de cada família. Ver o comentário das faces em `public/style.css`. */
export const PESO = { titulo: 600, texto: 400 } as const;

/**
 * Espaçamento extra entre letras, em fração do tamanho da fonte.
 *
 * Não está no `Texto espec.txt`, e foi medido: nas referências o local e a
 * pílula saem ~5% mais estreitos do que a Titillium no tamanho da
 * especificação, com a altura das letras igual — é tracking de -2%, o ajuste
 * comum do Figma. Título e subtítulo batem sem ajuste nenhum.
 */
export const TRACKING = { local: -0.02, parceiro: -0.02 } as const;

export interface Caixa {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * O card da capa: o `Card Capa.svg`, 997×534, a 22px da base.
 *
 * Os dois cards estão onde o designer os pôs nas referências — o final não é
 * centrado, fica 5px à direita e abaixo. Conferido sobrepondo a arte gerada
 * à referência em 20/10/2026: com o card centrado, o logo e a seta saíam
 * deslocados em relação ao material aprovado.
 */
export const CARD_CAPA = { x: 36, y: 794, w: 997, h: 534, raio: 45 } as const;

/** O card do slide final: o `Card final.svg`, 1008×1305. */
export const CARD_FINAL = { x: 41, y: 27, w: 1008, h: 1305, raio: 45 } as const;

/** A tag "we make tech simple_" das internas. */
export const TAG: Caixa = { x: 33, y: 1285, w: 266, h: 42 };

/** A seta das internas. */
export const SETA: Caixa = { x: 765, y: 1240, w: 296, h: 84 };

/**
 * O desfoque por trás dos cards.
 *
 * Nas referências o que está DENTRO do card aparece desfocado e o que está
 * fora, nítido — é vidro, não véu. Os SVGs só têm a cor do vidro; o desfoque
 * é por conta do código.
 */
export const DESFOQUE = { capa: 18, final: 26 } as const;

// ------------------------------------------------------------ o carrossel

/** Teto do Instagram: dez itens. Capa + internas + final. */
export const MAX_SLIDES = 10;
export const MAX_INTERNAS = MAX_SLIDES - 2;

/** Quantas fotos cabem num slide: empilhadas, em faixas de mesma altura. */
export type Faixas = 1 | 2 | 3;

export interface FotoNaArte {
  /** `media_assets.id`, ou null enquanto ninguém escolheu. */
  mediaId: number | null;
  /**
   * Onde o recorte cai, de 0 a 100, no eixo que sobra.
   *
   * Foto alta numa faixa de 450px perde quase tudo na vertical, e o centro
   * nem sempre é onde está o rosto. 50 é o centro.
   */
  foco: number;
}

export interface Capa {
  fotos: FotoNaArte[];
  /** Linha pequena depois do `//`: o lugar, ou o mote. */
  local: string;
  /** A pílula da direita: "Nex Lab x Parceiro". Vazio = sem pílula. */
  parceiro: string;
  titulo: string;
  subtitulo: string;
}

export interface Interna {
  fotos: FotoNaArte[];
}

export interface Carrossel {
  capa: Capa;
  internas: Interna[];
}

export type Slide =
  | { tipo: "capa"; fotos: FotoNaArte[]; capa: Capa }
  | { tipo: "interna"; indice: number; fotos: FotoNaArte[] }
  | {
      tipo: "final";
      /**
       * O fundo do final são as fotos do slide ANTERIOR, por trás do vidro —
       * é o que a referência `carrossel final.jpg` faz. Fica automático: o
       * final não tem nada para escolher.
       */
      fotos: FotoNaArte[];
    };

export function fotoVazia(): FotoNaArte {
  return { mediaId: null, foco: 50 };
}

export function carrosselVazio(): Carrossel {
  return {
    capa: {
      fotos: [fotoVazia()],
      local: "",
      parceiro: "",
      titulo: "",
      subtitulo: ""
    },
    internas: [{ fotos: [fotoVazia(), fotoVazia()] }]
  };
}

/**
 * Ajusta a lista de fotos a um número de faixas, PRESERVANDO as escolhidas.
 *
 * Trocar de 3 para 2 faixas não pode jogar fora a foto da terceira se ela for
 * a única escolhida; só corta o excesso pelo fim.
 */
export function comFaixas(fotos: FotoNaArte[], n: Faixas): FotoNaArte[] {
  const out = fotos.slice(0, n);
  while (out.length < n) out.push(fotoVazia());
  return out;
}

/** A ordem de saída. É a ordem do carrossel no Instagram. */
export function sequencia(c: Carrossel): Slide[] {
  const internas = c.internas.slice(0, MAX_INTERNAS);
  const ultima = internas.at(-1)?.fotos ?? c.capa.fotos;
  return [
    { tipo: "capa", fotos: c.capa.fotos, capa: c.capa },
    ...internas.map((i, indice): Slide => ({
      tipo: "interna",
      indice,
      fotos: i.fotos
    })),
    { tipo: "final", fotos: ultima }
  ];
}

/**
 * As faixas de foto de um slide, de cima para baixo.
 *
 * Alturas inteiras e somando exatamente a altura da arte — 1350 divide por 1,
 * 2 e 3, mas a regra fica explícita para não depender disso: sobra de
 * arredondamento vai para a última faixa, nunca vira uma linha vazia.
 */
export function faixas(n: number): Caixa[] {
  const k = Math.max(1, Math.floor(n));
  const h = Math.floor(ALTURA / k);
  return Array.from({ length: k }, (_, i) => ({
    x: 0,
    y: i * h,
    w: LARGURA,
    h: i === k - 1 ? ALTURA - i * h : h
  }));
}

/**
 * Onde desenhar a imagem para cobrir a caixa sem deformar (`cover`).
 *
 * O excesso é cortado no eixo que sobra, e `foco` decide onde o corte cai:
 * 0 mostra o começo (topo ou esquerda), 100 o fim, 50 o centro.
 */
export function recorte(
  imagem: { w: number; h: number },
  caixa: Caixa,
  foco: number
): Caixa {
  const escala = Math.max(caixa.w / imagem.w, caixa.h / imagem.h);
  const w = imagem.w * escala;
  const h = imagem.h * escala;
  const f = Math.min(100, Math.max(0, foco)) / 100;
  return {
    x: caixa.x + (caixa.w - w) * f,
    y: caixa.y + (caixa.h - h) * f,
    w,
    h
  };
}

// ----------------------------------------------------------------- a capa

/**
 * As posições do texto da capa.
 *
 * `y` é sempre a LINHA DE BASE, como o canvas desenha com
 * `textBaseline = "alphabetic"`. Tiradas das referências `carrossel capa uma
 * imagem.jpg` e `Capa variação duas imagens.jpg`, que concordam entre si.
 */
export const TEXTO_CAPA = {
  local: { x: CARD_CAPA.x + 82, y: CARD_CAPA.y + 64 },
  titulo: { x: CARD_CAPA.x + 40, y: CARD_CAPA.y + 258 },
  subtitulo: { x: CARD_CAPA.x + 40, y: CARD_CAPA.y + 326 },
  /** A pílula encosta à direita, a 18px da borda do card — alinhada com a linha. */
  pilula: {
    direita: CARD_CAPA.x + 979,
    y: CARD_CAPA.y + 21,
    h: 63,
    respiro: 43,
    linhaDeBase: CARD_CAPA.y + 21 + 45
  },
  /** A linha que segue o subtítulo até a borda. */
  linha: {
    y: CARD_CAPA.y + 310,
    fim: CARD_CAPA.x + 979,
    distancia: 14,
    /** Mais curta que isto, não vale desenhar: vira um tracinho solto. */
    minimo: 40,
    espessura: 1.5
  }
} as const;

/** Largura máxima do título: a do card, menos a mesma margem dos dois lados. */
export const TITULO_MAX = CARD_CAPA.w - 40 * 2;
/** O subtítulo vai até onde a linha terminaria. */
export const SUBTITULO_MAX = TEXTO_CAPA.linha.fim - TEXTO_CAPA.subtitulo.x;
/** Abaixo disto o título encolhido já não se parece com o sistema. */
export const TITULO_MIN = 72;

/**
 * O tamanho do título para caber numa linha.
 *
 * Recebe a largura MEDIDA no tamanho da especificação — medir é coisa do
 * canvas, decidir é daqui. Título que cabe sai no tamanho da especificação;
 * o que não cabe encolhe na proporção exata, nunca quebra linha: o card tem
 * altura fixa e uma segunda linha invadiria o subtítulo.
 */
export function tamanhoQueCabe(
  larguraNoTamanhoBase: number,
  base: number,
  maximo: number
): number {
  if (larguraNoTamanhoBase <= maximo || larguraNoTamanhoBase <= 0) return base;
  // Arredonda PARA BAIXO em meio pixel: arredondar para cima devolveria um
  // tamanho que passa do limite por uma fração.
  return Math.floor(((base * maximo) / larguraNoTamanhoBase) * 2) / 2;
}

/** A pílula do parceiro, dada a largura medida do texto. `null` sem texto. */
export function pilula(larguraTexto: number): Caixa | null {
  if (larguraTexto <= 0) return null;
  const p = TEXTO_CAPA.pilula;
  const w = larguraTexto + p.respiro * 2;
  return { x: p.direita - w, y: p.y, w, h: p.h };
}

/**
 * A linha depois do subtítulo, dado onde o texto termina.
 *
 * Sem subtítulo, a linha começa onde o texto começaria — na referência ela é
 * parte do card, não enfeite do texto.
 */
export function linhaDoSubtitulo(
  fimDoTexto: number | null
): { x1: number; x2: number; y: number } | null {
  const l = TEXTO_CAPA.linha;
  const x1 =
    fimDoTexto === null ? TEXTO_CAPA.subtitulo.x : fimDoTexto + l.distancia;
  if (l.fim - x1 < l.minimo) return null;
  return { x1, x2: l.fim, y: l.y };
}

// -------------------------------------------------------------- conferência

export interface Pendencia {
  /** Índice do slide na sequência — 0 é a capa. */
  slide: number;
  mensagem: string;
}

/**
 * O que impede de gerar.
 *
 * Foto faltando NÃO é aviso, é bloqueio: o slot vazio sai como um retângulo
 * escuro, e ele iria para o Instagram. Título também: capa sem título não é
 * capa. O resto — local, parceiro, subtítulo — é opcional no sistema.
 */
export function pendencias(c: Carrossel): Pendencia[] {
  const out: Pendencia[] = [];
  const seq = sequencia(c);

  if (c.capa.titulo.trim() === "") {
    out.push({ slide: 0, mensagem: "A capa está sem título." });
  }
  if (c.internas.length > MAX_INTERNAS) {
    out.push({
      slide: 0,
      mensagem:
        `O Instagram aceita até ${MAX_SLIDES} itens no carrossel: capa, ` +
        `${MAX_INTERNAS} internas e o final. Há ${c.internas.length} internas.`
    });
  }

  seq.forEach((s, i) => {
    if (s.tipo === "final") return; // repete as fotos do anterior
    const faltam = s.fotos.filter((f) => f.mediaId === null).length;
    if (faltam > 0) {
      const nome = s.tipo === "capa" ? "A capa" : `A interna ${s.indice + 1}`;
      out.push({
        slide: i,
        mensagem: `${nome} está com ${faltam} foto(s) faltando.`
      });
    }
  });

  return out;
}
